import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Camera, Video, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

export default function ProofGallery({ proofs }) {
  const [selectedProof, setSelectedProof] = useState(null);
  const [failedUrls, setFailedUrls] = useState([]);

  // Deduplicate by file_url to prevent rendering identical media files if they exist in the db
  const uniqueProofs = [];
  const seenUrls = new Set();
  (proofs || []).forEach(proof => {
    if (proof && proof.file_url && !seenUrls.has(proof.file_url)) {
      seenUrls.add(proof.file_url);
      uniqueProofs.push(proof);
    }
  });

  const visibleProofs = uniqueProofs.filter(proof => !failedUrls.includes(proof.file_url));

  if (!proofs || proofs.length === 0 || visibleProofs.length === 0) {
    return (
      <Card className="backdrop-blur-sm bg-white/80 dark:bg-zinc-900/80 border-zinc-200 dark:border-zinc-800">
        <CardContent className="p-12 text-center">
          <Camera className="w-16 h-16 mx-auto text-zinc-300 dark:text-zinc-600 mb-4" />
          <p className="text-zinc-500 dark:text-zinc-400">No delivery proofs uploaded yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="backdrop-blur-sm bg-white/80 dark:bg-zinc-900/80 border-zinc-200/80 dark:border-zinc-800/80 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-zinc-900 dark:text-white">
            <Camera className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Delivery Proofs ({visibleProofs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {visibleProofs.map((proof) => (
              <div
                key={proof._id || proof.file_url}
                className="relative group cursor-pointer rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                onClick={() => setSelectedProof(proof)}
              >
                {proof.proof_type === "photo" ? (
                  <img
                    src={proof.file_url}
                    alt="Delivery proof"
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                    onError={() => setFailedUrls((prev) => [...prev, proof.file_url])}
                  />
                ) : (
                  <div className="relative w-full h-48 bg-zinc-950 flex items-center justify-center overflow-hidden">
                    <video
                      src={proof.file_url}
                      className="w-full h-full object-cover opacity-75 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
                      muted
                      playsInline
                      loop
                      onMouseOver={(e) => e.target.play().catch(() => {})}
                      onMouseOut={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                      onError={(e) => {
                        console.warn("Video failed to load in ProofGallery grid:", proof.file_url);
                        if (proof.file_url && proof.file_url.includes('amazonaws.com')) {
                          e.target.src = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4';
                          e.target.load();
                        } else {
                          setFailedUrls((prev) => [...prev, proof.file_url]);
                        }
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors duration-300">
                      <div className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full border border-white/30 text-white shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                        <Video className="w-6 h-6 fill-white" />
                      </div>
                    </div>
                  </div>
                )}

                {proof.is_selected && (
                  <Badge className="absolute top-2 right-2 bg-yellow-500 border-yellow-600 text-white">
                    <Star className="w-3 h-3 mr-1 fill-white" />
                    Best Quality
                  </Badge>
                )}



                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selectedProof} onOpenChange={() => setSelectedProof(null)}>
        <DialogContent className="max-w-4xl bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-900 dark:text-white">Delivery Proof</DialogTitle>
          </DialogHeader>
          {selectedProof && (
            <div className="space-y-4">
              {selectedProof.proof_type === "photo" ? (
                <img
                  src={selectedProof.file_url}
                  alt="Delivery proof"
                  className="w-full rounded-lg"
                />
              ) : (
                <video
                  src={selectedProof.file_url}
                  controls
                  className="w-full rounded-lg"
                  onError={(e) => {
                    if (selectedProof.file_url && selectedProof.file_url.includes('amazonaws.com')) {
                      e.target.src = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4';
                      e.target.load();
                    }
                  }}
                />
              )}
              {selectedProof.upload_notes && (
                <p className="text-sm text-zinc-600 dark:text-zinc-400">{selectedProof.upload_notes}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}